/**
 * The one social-profile text input.
 *
 * Every surface that collects a social profile renders this component so the
 * `@` affordance, placeholder and handle normalization can never drift per
 * page. Field metadata comes from `@/lib/social-placeholders`; storage and the
 * public URL builder are unchanged.
 */
import {
  socialFieldCommit,
  socialFieldDisplay,
  type SocialNetwork,
} from "@/lib/social-handle";
import { socialFieldDef } from "@/lib/social-placeholders";

export function SocialHandleInput({
  network,
  value,
  onChange,
  id,
  disabled,
  className,
  wrapperClassName,
}: {
  network: SocialNetwork;
  value: string;
  onChange: (next: string) => void;
  id?: string;
  disabled?: boolean;
  /** Applied to the inner <input> so host forms keep their own typography. */
  className?: string;
  wrapperClassName?: string;
}) {
  const def = socialFieldDef(network);
  const display = socialFieldDisplay(network, value);

  return (
    <div
      className={
        wrapperClassName ??
        "flex items-center gap-1 rounded-sm border border-border bg-background px-3 py-2 focus-within:border-foreground"
      }
    >
      {display.showAtPrefix && (
        <span className="text-sm text-muted-foreground">@</span>
      )}
      <input
        id={id}
        aria-label={def.label}
        value={display.text}
        disabled={disabled}
        placeholder={def.placeholder}
        onChange={(e) => onChange(socialFieldCommit(network, e.target.value))}
        className={
          className ??
          "flex-1 bg-transparent text-base md:text-sm focus:outline-none disabled:opacity-50"
        }
      />
    </div>
  );
}
